import React, { useState, useEffect, useRef, useLayoutEffect } from 'react';
import { createPortal } from 'react-dom';

const PlayerSearchSelect: React.FC<{
  players: { id: string; name: string }[];
  value: string;
  onChange: (id: string) => void;
  required?: boolean;
  inputClasses: string;
}> = ({ players, value, onChange, required, inputClasses }) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState<{ top: number; left: number; width: number } | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Sync display text when value changes externally (e.g. form reset)
  const selectedName = players.find((p) => p.id === value)?.name || '';
  useEffect(() => {
    setQuery(selectedName);
  }, [selectedName]);

  // Position the (portalled) dropdown under the input. Recompute while open so it
  // tracks scrolling/resizing. The portal escapes ancestor `overflow` clipping
  // (the users table uses overflow-x-auto and Card uses overflow-hidden), which
  // otherwise cut off the bottom of the list.
  useLayoutEffect(() => {
    if (!open) return;
    const update = () => {
      const el = inputRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setRect({ top: r.bottom, left: r.left, width: r.width });
    };
    update();
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      const insideWrapper = wrapperRef.current?.contains(target);
      const insideDropdown = dropdownRef.current?.contains(target);
      if (!insideWrapper && !insideDropdown) {
        setOpen(false);
        // Reset text to selected player if user didn't pick
        if (!value) setQuery('');
        else setQuery(selectedName);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [value, selectedName]);

  const filtered = query
    ? players.filter((p) => p.name.toLowerCase().includes(query.toLowerCase()))
    : players;

  const dropdown =
    open && rect
      ? createPortal(
          <div
            ref={dropdownRef}
            style={{ position: 'fixed', top: rect.top, left: rect.left, width: rect.width }}
            className="z-50 mt-1"
          >
            {filtered.length > 0 ? (
              <ul className="max-h-48 overflow-y-auto rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-lg">
                {filtered.map((p) => (
                  <li
                    key={p.id}
                    onMouseDown={() => {
                      onChange(p.id);
                      setQuery(p.name);
                      setOpen(false);
                    }}
                    className={`cursor-pointer px-3 py-2 text-sm text-gray-900 dark:text-gray-100 hover:bg-blue-50 dark:hover:bg-blue-900/30 ${p.id === value ? 'bg-blue-50 dark:bg-blue-900/30 font-medium' : ''}`}
                  >
                    {p.name}
                  </li>
                ))}
              </ul>
            ) : (
              query && (
                <div className="rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-lg px-3 py-2 text-sm text-gray-500 dark:text-gray-400">
                  No players found
                </div>
              )
            )}
          </div>,
          document.body,
        )
      : null;

  return (
    <div ref={wrapperRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        required={required}
        placeholder="Search player..."
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          if (!e.target.value) onChange('');
        }}
        onFocus={() => setOpen(true)}
        className={inputClasses}
        // Prevent form validation on the text input — the hidden input carries the real value
        onInvalid={(e) => {
          if (!value) (e.target as HTMLInputElement).setCustomValidity('Please select a player');
          else (e.target as HTMLInputElement).setCustomValidity('');
        }}
        onInput={(e) => (e.target as HTMLInputElement).setCustomValidity('')}
      />
      {/* Hidden input carries the actual player ID for form validation */}
      <input type="hidden" value={value} required={required} />
      {dropdown}
    </div>
  );
};

export default PlayerSearchSelect;
